import React, { createContext, useContext, useState, useCallback } from "react";
import api from "@/lib/api";

const ES = {
  "Command Center": "Centro de Mando", "Dashboard": "Panel", "Analytics": "Analíticas",
  "E-Sign Inbox": "Bandeja de Firmas", "Dispatch Board": "Tablero de Despacho",
  "Carriers": "Transportistas", "Carrier Check": "Verificar Transportista",
  "Shippers": "Clientes de Carga", "My Customers": "Mis Clientes", "CRM": "CRM",
  "Brokerage Loads": "Cargas de Brokerage", "Customer Quotes": "Cotizaciones de Clientes",
  "DNU Requests": "Solicitudes DNU", "Claims": "Reclamos", "Documents": "Documentos",
  "Accounting Queue": "Cola de Contabilidad", "Agreements": "Acuerdos", "Heat Map": "Mapa de Calor",
  "Rate Tools": "Herramientas de Tarifas", "AI Assistant": "Asistente IA", "SMS Alerts": "Alertas SMS",
  "Accounting": "Contabilidad", "Commission Ledger": "Libro de Comisiones", "Loads": "Cargas",
  "Carrier Apps": "Solicitudes de Transportistas", "Shipper Quotes": "Cotizaciones de Clientes",
  "Invoices": "Facturas", "Promotions": "Promociones", "Signed Agreements": "Acuerdos Firmados",
  "Compliance": "Cumplimiento", "SOP & Training": "SOP y Capacitación", "My Training": "Mi Capacitación",
  "Team": "Equipo", "Organization": "Organización", "Data Hygiene": "Higiene de Datos",
  "Promoters": "Promotores", "Portal": "Portal",
  "Change Password": "Cambiar Contraseña", "Sign Out": "Cerrar Sesión", "Password": "Contraseña",
  "Welcome": "Bienvenido", "Carrier Dashboard": "Panel del Transportista",
  "Shipper Dashboard": "Panel del Cliente",
  "Your loads, your invoices, your rate.": "Tus cargas, tus facturas, tu tarifa.",
  "Your freight quotes and shipment history.": "Tus cotizaciones de carga e historial de envíos.",
  "Carrier Background Check": "Verificación de Antecedentes del Transportista",
  "Run check": "Verificar", "Checking…": "Verificando…",
  "Quote Requests": "Solicitudes de Cotización", "Pending": "Pendiente",
  // Command Center
  "Owner / Admin": "Propietario / Admin", "MFG Command Center": "Centro de Mando MFG",
  "Management by exception — every number is clickable and opens the exact records behind it.": "Gestión por excepción — cada número es clicable y abre los registros exactos detrás de él.",
  "Loads created": "Cargas creadas", "In transit now": "En tránsito ahora", "Delivered": "Entregadas",
  "In-transit linehaul": "Linehaul en tránsito", "Gross freight moved": "Flete bruto movido",
  "MFG revenue": "Ingresos MFG", "Dispatch revenue": "Ingresos de despacho",
  "Brokerage gross margin": "Margen bruto de brokerage", "Outstanding A/R": "Cuentas por cobrar",
  "Carrier pay outstanding": "Pago a transportistas pendiente", "Documents missing": "Documentos faltantes",
  "Compliance problems": "Problemas de cumplimiento", "Open claims": "Reclamos abiertos",
  "Needs Your Attention": "Necesita Tu Atención",
  "Nothing needs your attention. Clean board.": "Nada necesita tu atención. Tablero limpio.",
  "Exception drill-down": "Detalle de la excepción", "Loading command center…": "Cargando centro de mando…",
  "Today": "Hoy", "7 Days": "7 Días", "30 Days": "30 Días", "90 Days": "90 Días", "YTD": "Año", "Custom": "Personalizado",
  "Monthly freight-moved goals": "Metas mensuales de flete movido", "Set targets": "Definir metas",
  "Save": "Guardar", "Cancel": "Cancelar", "no target": "sin meta",
  "Dispatch": "Despacho", "Brokerage": "Brokerage",
  "ahead by": "adelante por", "behind by": "atrasado por", "→ on pace": "→ al ritmo",
  // Portal dashboard (Overview)
  "Overview": "Resumen",
  "Real-time snapshot of operations at Montana Freight Group.": "Vista en tiempo real de las operaciones de Montana Freight Group.",
  "Portal Modules": "Módulos del Portal", "All the tools you need.": "Todas las herramientas que necesitas.",
  "Your Role": "Tu Rol", "Contact & Support": "Contacto y Soporte", "Open": "Abrir",
  "Pending Carrier Apps": "Solicitudes Pendientes", "Approved Carriers": "Transportistas Aprobados",
  "New Shipper Quotes": "Cotizaciones Nuevas", "Posted Loads": "Cargas Publicadas",
  "In Transit": "En Tránsito", "Pending Invoices": "Facturas Pendientes",
  "Total Quotes": "Cotizaciones Totales", "Contact Messages": "Mensajes de Contacto",
  "Load Board": "Tablero de Cargas", "Carrier Applications": "Solicitudes de Transportistas",
  "Invoices & Payments": "Facturas y Pagos", "Team Management": "Gestión del Equipo",
  "Post loads, assign carriers, track status through delivery.": "Publica cargas, asigna transportistas y sigue el estado hasta la entrega.",
  "Review, approve or reject onboarding applications & docs.": "Revisa, aprueba o rechaza solicitudes y documentos de ingreso.",
  "Every quote request from shippers, newest first.": "Cada solicitud de cotización de clientes, las más nuevas primero.",
  "Auto-calc 7% or 8% fee (−0.5% fast pay), mark paid/overdue, weekly settlements.": "Cálculo automático de tarifa 7% u 8% (−0.5% pago rápido), marcar pagado/vencido, liquidaciones semanales.",
  "Standard Operating Procedures, training videos, PDFs.": "Procedimientos operativos estándar, videos de capacitación, PDFs.",
  "Create administrators, dispatchers, agents & other employees.": "Crea administradores, despachadores, agentes y otros empleados.",
  "Overseas promoters — accounts, referral codes, bonuses & 60-day progress.": "Promotores en el exterior — cuentas, códigos de referido, bonos y progreso de 60 días.",
  "FMCSA background check — authority, safety scores & insurance by MC/DOT.": "Verificación FMCSA — autoridad, puntajes de seguridad y seguro por MC/DOT.",
};

const LangContext = createContext({ lang: "en", setLang: () => {}, t: (s) => s });

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("mfg_lang") || "en");
  const setLang = useCallback((l) => {
    setLangState(l);
    localStorage.setItem("mfg_lang", l);
    api.patch("/me/language", { language: l }).catch(() => {});
  }, []);
  const t = useCallback((s) => (lang === "es" ? (ES[s] || s) : s), [lang]);
  return <LangContext.Provider value={{ lang, setLang, t }}>{children}</LangContext.Provider>;
}

export const useLang = () => useContext(LangContext);

const USFlag = () => (
  <svg viewBox="0 0 24 16" className="w-6 h-4 rounded-[2px] block">
    <rect width="24" height="16" fill="#B22234" />
    {[1, 3, 5, 7, 9, 11].map((i) => <rect key={i} y={i * 16 / 13} width="24" height={16 / 13} fill="#fff" />)}
    <rect width="10" height={16 * 7 / 13} fill="#3C3B6E" />
    {[2, 5, 8].map((x) => [1.5, 4, 6.5].map((y) => <circle key={`${x}${y}`} cx={x} cy={y} r="0.55" fill="#fff" />))}
  </svg>
);

const COFlag = () => (
  <svg viewBox="0 0 24 16" className="w-6 h-4 rounded-[2px] block">
    <rect width="24" height="8" fill="#FCD116" />
    <rect y="8" width="24" height="4" fill="#003893" />
    <rect y="12" width="24" height="4" fill="#CE1126" />
  </svg>
);

export function LangToggle() {
  const { lang, setLang } = useLang();
  const btn = (code, Flag, label) => (
    <button onClick={() => setLang(code)} title={label} aria-label={label}
      data-testid={`lang-${code}`}
      className={`p-1 rounded-md transition-all ${lang === code ? "ring-2 ring-[#0A84FF] bg-[#0A84FF]/10 scale-105" : "opacity-50 hover:opacity-100"}`}>
      <Flag />
    </button>
  );
  return (
    <div className="flex items-center gap-1" data-testid="lang-toggle">
      {btn("en", USFlag, "English")}
      {btn("es", COFlag, "Español")}
    </div>
  );
}
